import { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { SearchableCombobox } from "../../components/ui/SearchableCombobox";
import {
  Laptop,
  Smartphone,
  Armchair,
  Monitor,
  Printer,
  Package,
  Car,
  Cpu,
  Plus,
  Eye,
  Building2,
  UserRound,
  Home,
  Briefcase,
  Clock,
  RefreshCw,
  Undo2,
  AlertTriangle,
  X,
  Pencil,
  Trash2,
  ArrowRightLeft,
  Check,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { DateField } from "../../components/ui/DateField";
import { getEmployees } from "../../api/employees";
import { format } from "date-fns";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { useAccess } from "../../contexts/AccessContext";
import {
  acceptAsset,
  addAssetCategory,
  assignAsset,
  createAsset,
  createAssetRequest,
  decideAssetRequest,
  deleteAsset,
  getAsset,
  getAssetCategories,
  getAssets,
  getAssetRequests,
  getRecentMovements,
  rejectAsset,
  returnAsset,
  updateAsset,
  withdrawAssetRequest,
  type Asset,
  type AssetCreatePayload,
  type AssetMovement,
  type AssetRequest,
  type AssetRequestKind,
  type AssetScope,
  type AssetStatus,
  type AssetSummary,
  type AssetType,
} from "../../api/assets";

// Mirrors the backend's _can_manage (ELEVATED_ROLES + manager). Frontend
// gating is UX only — every manage endpoint re-checks the role server-side.
const MANAGE_ROLES = [
  "admin", "hr", "hr head", "hr admin", "hod", "owner", "superadmin",
  "director", "founder", "manager",
];

type TabKey = "my" | "all" | "requests" | "approvals";

interface EmployeeLite {
  id: string;
  user_id?: string;
  employeeID?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

const empLabel = (e: EmployeeLite) =>
  `${[e.first_name, e.last_name].filter(Boolean).join(" ") || e.email || e.id}${
    e.employeeID ? ` (${e.employeeID})` : ""
  }`;

const EMPTY_FORM: AssetCreatePayload = {
  name: "",
  category: "",
  asset_code: "",
  serial_no: "",
  description: "",
  asset_type: "OFFICE",
  used_for: "WORK",
  location: "",
  purchase_date: null,
  purchase_price: null,
  vendor_name: "",
  warranty_until: null,
  notes: "",
  assign_to_empt_id: "",
};
type TypeFilter = "ALL" | AssetType;

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return format(new Date(s), "dd MMM yyyy");
  } catch {
    return String(s);
  }
}

// FastAPI 422s return `detail` as an array of objects — never hand those to a
// toast/JSX directly (mirrors the Expenses page helper).
function errMsg(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => (x && typeof x === "object" ? x.msg : typeof x === "string" ? x : null))
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

function categoryIcon(category?: string | null) {
  const c = (category || "").toLowerCase();
  if (c.includes("computer") || c.includes("laptop") || c.includes("desktop") || c.includes("data processing"))
    return Laptop;
  if (c.includes("mobile") || c.includes("phone") || c.includes("tablet")) return Smartphone;
  if (c.includes("chair") || c.includes("furniture") || c.includes("desk") || c.includes("fitting"))
    return Armchair;
  if (c.includes("monitor")) return Monitor;
  if (c.includes("printer") || c.includes("scanner") || c.includes("office equipment"))
    return Printer;
  if (c.includes("motor") || c.includes("vehicle") || c.includes("aircraft") || c.includes("ship"))
    return Car;
  if (c.includes("building") || c.includes("road") || c.includes("bridge"))
    return Building2;
  if (c.includes("electric") || c.includes("plant") || c.includes("machinery"))
    return Cpu;
  return Package;
}

function statusBadgeClass(status: AssetStatus): string {
  switch (status) {
    case "IN_USE":
      return "bg-green-100 text-green-700";
    case "AVAILABLE":
      return "bg-blue-100 text-blue-700";
    case "UNDER_REPAIR":
      return "bg-amber-100 text-amber-700";
    case "RETIRED":
    default:
      return "bg-gray-100 text-gray-600";
  }
}

const STATUS_LABEL: Record<AssetStatus, string> = {
  IN_USE: "In Use",
  AVAILABLE: "Available",
  UNDER_REPAIR: "Under Repair",
  RETIRED: "Retired",
};

const REQUEST_STATUS_BADGE: Record<string, string> = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-green-100 text-green-700",
  REJECTED: "bg-red-100 text-red-700",
  WITHDRAWN: "bg-gray-100 text-gray-600",
};

const REQUEST_KIND_LABEL: Record<AssetRequestKind, string> = {
  NEW: "New Asset",
  TRANSFER: "Transfer",
  RETURN: "Return",
  ISSUE: "Repair Request",
};

const fmtMoney = (n: number | null | undefined) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}`;

// One-line repair summary for request lists ("Third-party · ₹8,000" / "By company").
const repairSummary = (r: AssetRequest): string | null => {
  if (r.kind !== "ISSUE" || !r.repair_mode) return null;
  return r.repair_mode === "THIRD_PARTY"
    ? `Third-party repair · ${fmtMoney(r.repair_cost)}`
    : "Repair by company";
};

export default function Assets() {
  const { user } = useAuth();
  const { hasAccess, canManage } = useAccess();

  const roleCanManage = MANAGE_ROLES.includes((user?.role || "").toLowerCase());
  // Access profile can restrict a manage-role to view-only; it can't grant
  // manage beyond the role because the backend checks the role itself.
  const manageAllowed = roleCanManage && (!hasAccess || canManage("assets"));

  const [tab, setTab] = useState<TabKey>("my");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [empFilter, setEmpFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const [assets, setAssets] = useState<Asset[]>([]);
  const [summary, setSummary] = useState<AssetSummary | null>(null);
  const [requests, setRequests] = useState<AssetRequest[]>([]);
  const [loading, setLoading] = useState(true);

  // View dialog
  const [viewAsset, setViewAsset] = useState<Asset | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Request Asset dialog
  const [requestOpen, setRequestOpen] = useState(false);
  const [reqKind, setReqKind] = useState<AssetRequestKind>("NEW");
  const [reqCategory, setReqCategory] = useState("");
  const [reqAssetId, setReqAssetId] = useState("");
  const [reqDetails, setReqDetails] = useState("");
  // Repair (ISSUE) requests — who arranges it; cost only for third-party.
  const [reqRepairMode, setReqRepairMode] = useState<"" | "THIRD_PARTY" | "COMPANY">("");
  const [reqRepairCost, setReqRepairCost] = useState("");
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  // ---- manage state (Add/Edit, Assign, Approvals) — asset managers only ----
  const [formOpen, setFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [form, setForm] = useState<AssetCreatePayload>({ ...EMPTY_FORM });
  const [formSaving, setFormSaving] = useState(false);

  const [assignTarget, setAssignTarget] = useState<Asset | null>(null);
  const [assignEmpId, setAssignEmpId] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [assignSaving, setAssignSaving] = useState(false);

  const [employees, setEmployees] = useState<EmployeeLite[]>([]);
  const [movements, setMovements] = useState<AssetMovement[]>([]);
  const [approvals, setApprovals] = useState<AssetRequest[]>([]);
  const [decisionReq, setDecisionReq] = useState<AssetRequest | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [decisionSaving, setDecisionSaving] = useState(false);

  // Assignee accept/reject — reject needs a mandatory reason.
  const [rejectTarget, setRejectTarget] = useState<Asset | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectSaving, setRejectSaving] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);

  const scope: AssetScope = tab === "all" ? "all" : "my";

  const loadAssets = useCallback(() => {
    setLoading(true);
    getAssets({ scope })
      .then((res) => {
        setAssets(Array.isArray(res.assets) ? res.assets : []);
        if (res.summary) setSummary(res.summary);
      })
      .catch((e) => toast.error(errMsg(e, "Failed to load assets")))
      .finally(() => setLoading(false));
  }, [scope]);

  const loadRequests = useCallback(() => {
    getAssetRequests("mine")
      .then(setRequests)
      .catch(() => setRequests([]));
  }, []);

  const loadApprovals = useCallback(() => {
    getAssetRequests("pending")
      .then(setApprovals)
      .catch(() => setApprovals([]));
  }, []);

  useEffect(() => {
    if (tab === "requests") {
      loadRequests();
    } else if (tab === "approvals") {
      loadApprovals();
    } else {
      loadAssets();
    }
  }, [tab, loadAssets, loadRequests, loadApprovals]);

  // Employee pick-list for the Add/Edit + Assign dialogs (managers only).
  useEffect(() => {
    if (!manageAllowed || employees.length > 0) return;
    if (!formOpen && !assignTarget) return;
    getEmployees()
      .then((r) => setEmployees(Array.isArray(r?.employees) ? r.employees : []))
      .catch(() => setEmployees([]));
  }, [manageAllowed, formOpen, assignTarget, employees.length]);

  // Categories also feed the Add/Edit form's suggestions.
  useEffect(() => {
    if (formOpen && categories.length === 0) {
      getAssetCategories().then(setCategories).catch(() => {});
    }
  }, [formOpen, categories.length]);

  // Load categories once, when the request dialog first opens.
  useEffect(() => {
    if (requestOpen && categories.length === 0) {
      getAssetCategories().then(setCategories).catch(() => {});
    }
  }, [requestOpen, categories.length]);

  // Managers: prefetch pending approvals so the tab badge is meaningful.
  useEffect(() => {
    if (manageAllowed) loadApprovals();
  }, [manageAllowed, loadApprovals]);

  // Recent movement feed — refreshed whenever the asset list changes (every
  // assign/transfer/return reloads assets, which re-runs this).
  useEffect(() => {
    getRecentMovements(6)
      .then(setMovements)
      .catch(() => setMovements([]));
  }, [assets]);

  // My in-use assets — the pick-list for RETURN / ISSUE requests.
  const [myAssets, setMyAssets] = useState<Asset[]>([]);
  useEffect(() => {
    if (!requestOpen || reqKind === "NEW") return;
    getAssets({ scope: "my" })
      .then((res) => setMyAssets(res.assets || []))
      .catch(() => setMyAssets([]));
  }, [requestOpen, reqKind]);

  const typeCounts = useMemo(() => {
    const office = assets.filter((a) => a.asset_type === "OFFICE").length;
    return { all: assets.length, office, personal: assets.length - office };
  }, [assets]);

  // Distinct option lists for the filter dropdowns / suggestions, drawn from
  // the currently-loaded assets so they always reflect what's on screen.
  const assetNameOptions = useMemo(
    () => Array.from(new Set(assets.map((a) => a.name).filter(Boolean))).sort(),
    [assets]
  );
  const locationOptions = useMemo(
    () => Array.from(new Set(assets.map((a) => a.location).filter(Boolean) as string[])).sort(),
    [assets]
  );
  const holderOptions = useMemo(
    () => Array.from(new Set(assets.map((a) => a.assigned_name).filter(Boolean) as string[])).sort(),
    [assets]
  );

  const filtersActive =
    typeFilter !== "ALL" || !!statusFilter || !!search.trim() || !!empFilter || !!locationFilter;

  const clearFilters = () => {
    setTypeFilter("ALL");
    setStatusFilter("");
    setSearch("");
    setEmpFilter("");
    setLocationFilter("");
  };

  const filteredAssets = useMemo(() => {
    let rows = assets;
    if (typeFilter !== "ALL") rows = rows.filter((a) => a.asset_type === typeFilter);
    if (statusFilter) rows = rows.filter((a) => a.status === statusFilter);
    if (locationFilter) rows = rows.filter((a) => (a.location || "") === locationFilter);
    const emp = empFilter.trim().toLowerCase();
    if (emp) {
      rows = rows.filter((a) =>
        [a.assigned_name, a.assigned_employeeID].some((v) => (v || "").toLowerCase().includes(emp))
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((a) =>
        [a.name, a.category, a.asset_code, a.serial_no, a.assigned_name, a.location]
          .some((v) => (v || "").toLowerCase().includes(q))
      );
    }
    return rows;
  }, [assets, typeFilter, statusFilter, search, empFilter, locationFilter]);

  const openView = (asset: Asset) => {
    setViewAsset(asset); // instant open with the row we have
    setViewLoading(true);
    getAsset(asset.id)
      .then((full) => setViewAsset(full))
      .catch(() => {})
      .finally(() => setViewLoading(false));
  };

  const resetRequestForm = () => {
    setReqKind("NEW");
    setReqCategory("");
    setReqAssetId("");
    setReqDetails("");
    setReqRepairMode("");
    setReqRepairCost("");
  };

  const submitRequest = async () => {
    if (reqKind === "NEW" && !reqCategory) {
      toast.error("Choose what kind of asset you need");
      return;
    }
    if (reqKind !== "NEW" && !reqAssetId) {
      toast.error("Choose the asset");
      return;
    }
    if (reqKind === "ISSUE") {
      if (!reqRepairMode) {
        toast.error("Choose who arranges the repair");
        return;
      }
      if (reqRepairMode === "THIRD_PARTY" && (!reqRepairCost || Number(reqRepairCost) < 0)) {
        toast.error("Enter the repair cost");
        return;
      }
    }
    setReqSubmitting(true);
    try {
      await createAssetRequest({
        kind: reqKind,
        category: reqKind === "NEW" ? reqCategory : undefined,
        asset_id: reqKind === "NEW" ? undefined : reqAssetId,
        details: reqDetails.trim() || undefined,
        repair_mode: reqKind === "ISSUE" ? (reqRepairMode as "THIRD_PARTY" | "COMPANY") : undefined,
        repair_cost:
          reqKind === "ISSUE" && reqRepairMode === "THIRD_PARTY"
            ? Number(reqRepairCost)
            : undefined,
      });
      toast.success("Request submitted");
      setRequestOpen(false);
      resetRequestForm();
      if (tab === "requests") loadRequests();
    } catch (e) {
      toast.error(errMsg(e, "Failed to submit request"));
    } finally {
      setReqSubmitting(false);
    }
  };

  const withdraw = async (id: string) => {
    try {
      await withdrawAssetRequest(id);
      toast.success("Request withdrawn");
      loadRequests();
    } catch (e) {
      toast.error(errMsg(e, "Failed to withdraw request"));
    }
  };

  // ---------------------------------------------- manage actions (admin) ----

  const openCreate = () => {
    setEditingAsset(null);
    setForm({ ...EMPTY_FORM });
    setFormOpen(true);
  };

  const openEdit = (a: Asset) => {
    setEditingAsset(a);
    setForm({
      name: a.name || "",
      category: a.category || "",
      asset_code: a.asset_code || "",
      serial_no: a.serial_no || "",
      description: a.description || "",
      asset_type: a.asset_type,
      used_for: a.used_for,
      location: a.location || "",
      purchase_date: a.purchase_date,
      purchase_price: a.purchase_price,
      vendor_name: a.vendor_name || "",
      warranty_until: a.warranty_until,
      notes: a.notes || "",
      assign_to_empt_id: "",
    });
    setFormOpen(true);
  };

  const setF = (field: keyof AssetCreatePayload, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const saveAsset = async () => {
    if (!(form.name || "").trim()) {
      toast.error("Asset name is required");
      return;
    }
    setFormSaving(true);
    try {
      // A typed-in category the org doesn't know yet is saved to its list
      // first (same pattern as expense natures).
      const cat = (form.category || "").trim();
      if (cat && !categories.some((c) => c.toLowerCase() === cat.toLowerCase())) {
        await addAssetCategory(cat).catch(() => {});
        setCategories([]);
      }
      const payload: AssetCreatePayload = {
        ...form,
        purchase_price:
          form.purchase_price === null || (form.purchase_price as any) === ""
            ? null
            : Number(form.purchase_price),
        purchase_date: form.purchase_date || null,
        warranty_until: form.warranty_until || null,
        assign_to_empt_id: form.assign_to_empt_id || null,
      };
      if (editingAsset) {
        const { assign_to_empt_id, ...rest } = payload;
        await updateAsset(editingAsset.id, rest);
        toast.success("Asset updated");
      } else {
        await createAsset(payload);
        toast.success("Asset registered");
      }
      setFormOpen(false);
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to save asset"));
    } finally {
      setFormSaving(false);
    }
  };

  const openAssign = (a: Asset) => {
    setAssignTarget(a);
    setAssignEmpId("");
    setAssignNote("");
  };

  const doAssign = async () => {
    if (!assignTarget) return;
    if (!assignEmpId) {
      toast.error("Choose an employee");
      return;
    }
    setAssignSaving(true);
    try {
      await assignAsset(assignTarget.id, {
        empt_id: assignEmpId,
        note: assignNote.trim() || undefined,
      });
      toast.success(assignTarget.assigned_empt_id ? "Asset transferred" : "Asset assigned");
      setAssignTarget(null);
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to assign asset"));
    } finally {
      setAssignSaving(false);
    }
  };

  const doReturn = async (a: Asset) => {
    try {
      await returnAsset(a.id);
      toast.success("Asset returned to stock");
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to return asset"));
    }
  };

  // --- assignee sign-off on a PENDING assignment (My Assets) ---
  const doAccept = async (a: Asset) => {
    setAcceptingId(a.id);
    try {
      await acceptAsset(a.id);
      toast.success("Asset accepted");
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to accept asset"));
    } finally {
      setAcceptingId(null);
    }
  };

  const openReject = (a: Asset) => {
    setRejectTarget(a);
    setRejectReason("");
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    if (!rejectReason.trim()) {
      toast.error("Please add a reason for rejecting");
      return;
    }
    setRejectSaving(true);
    try {
      await rejectAsset(rejectTarget.id, rejectReason.trim());
      toast.success("Asset rejected");
      setRejectTarget(null);
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to reject asset"));
    } finally {
      setRejectSaving(false);
    }
  };

  const doDelete = async (a: Asset) => {
    if (!window.confirm(`Delete "${a.name}"? It will disappear from the register.`)) return;
    try {
      await deleteAsset(a.id);
      toast.success("Asset deleted");
      loadAssets();
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete asset"));
    }
  };

  const openDecision = (r: AssetRequest) => {
    setDecisionReq(r);
    setDecisionNote("");
  };

  const submitDecision = async (status: "APPROVED" | "REJECTED") => {
    if (!decisionReq) return;
    setDecisionSaving(true);
    try {
      const res = await decideAssetRequest(
        decisionReq.id,
        status,
        decisionNote.trim() || undefined
      );
      toast.success(res.message || `Request ${status.toLowerCase()}`);
      setDecisionReq(null);
      loadApprovals();
    } catch (e) {
      toast.error(errMsg(e, "Failed to record the decision"));
    } finally {
      setDecisionSaving(false);
    }
  };

  const tabs: { key: TabKey; label: string }[] = [
    { key: "my", label: "My Assets" },
    ...(manageAllowed ? [{ key: "all" as TabKey, label: "All Assets" }] : []),
    { key: "requests", label: "My Requests" },
    ...(manageAllowed
      ? [{ key: "approvals" as TabKey, label: `Approvals${approvals.length ? ` (${approvals.length})` : ""}` }]
      : []),
  ];

  return (
    <div className="gt-themed p-6 space-y-6">
      <PageHeader
        title={<>Asset <em>Management</em></>}
        subtitle={<>View and manage assets <em>assigned to you</em>.</>}
        actions={
          <>
            {manageAllowed && (
              <button className="gt-btn" onClick={openCreate}>
                <Package size={16} /> Add Asset
              </button>
            )}
            <button
              className="gt-btn is-primary"
              onClick={() => {
                resetRequestForm();
                setRequestOpen(true);
              }}
            >
              <Plus size={16} /> Request Asset
            </button>
          </>
        }
      />

      {/* Stat tiles — always about MY assets, whatever tab is open */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="gt-stat">
          <div className="gt-stat-label"><Laptop size={14} /> Assets Assigned to Me</div>
          <div className="gt-stat-value">{summary?.assigned_to_me ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><Building2 size={14} /> Office Assets (Using)</div>
          <div className="gt-stat-value">{summary?.office_assets ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><UserRound size={14} /> Personal Assets (Using)</div>
          <div className="gt-stat-value">{summary?.personal_assets ?? "—"}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><Home size={14} /> Work From Home</div>
          <div className="gt-stat-value">
            {summary ? (summary.wfh_assets > 0 ? "Yes" : "No") : "—"}
            {summary && summary.wfh_assets > 0 && (
              <span className="text-xs font-normal text-gray-500 ml-2">
                using {summary.wfh_assets} asset{summary.wfh_assets > 1 ? "s" : ""}
              </span>
            )}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {tab === "approvals" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pending Asset Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {approvals.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">
                <Check className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                Nothing waiting for a decision.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Requested By</th>
                      <th className="py-2 pr-3">Request</th>
                      <th className="py-2 pr-3">Asset / Category</th>
                      <th className="py-2 pr-3">Details</th>
                      <th className="py-2 pr-3">Submitted</th>
                      <th className="py-2 pr-3 text-right">Decision</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {approvals.map((r) => (
                      <tr key={r.id}>
                        <td className="py-3 pr-3">
                          <div className="font-medium text-gray-900">{r.name || "—"}</div>
                          {r.employeeID && (
                            <div className="text-[11px] text-gray-400 font-mono">{r.employeeID}</div>
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          <Badge className="bg-gray-100 text-gray-700 rounded-md">
                            {REQUEST_KIND_LABEL[r.kind] || r.kind}
                          </Badge>
                          {repairSummary(r) && (
                            <div className="text-xs text-emerald-700 mt-1">{repairSummary(r)}</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-gray-600">
                          {r.kind === "NEW" ? (r.category || "—") : (r.asset_name || "—")}
                        </td>
                        <td className="py-3 pr-3 text-gray-600">
                          <div className="text-xs max-w-[280px] truncate" title={r.details || ""}>
                            {r.details || "—"}
                          </div>
                        </td>
                        <td className="py-3 pr-3 text-gray-600 text-xs whitespace-nowrap">
                          {fmtDate(r.submitted_on)}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          <button
                            className="gt-btn is-primary is-sm"
                            onClick={() => openDecision(r)}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : tab === "requests" ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">My Asset Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {requests.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm">
                <Clock className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                No asset requests yet. Use “Request Asset” to file one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Request</th>
                      <th className="py-2 pr-3">Asset</th>
                      <th className="py-2 pr-3">Submitted</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Decision</th>
                      <th className="py-2 pr-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {requests.map((r) => (
                      <tr key={r.id}>
                        <td className="py-3 pr-3">
                          <div className="font-medium text-gray-900">
                            {REQUEST_KIND_LABEL[r.kind] || r.kind}
                          </div>
                          {repairSummary(r) && (
                            <div className="text-xs text-emerald-700">{repairSummary(r)}</div>
                          )}
                          {(r.details || r.title) && (
                            <div className="text-xs text-gray-500 max-w-[320px] truncate">
                              {r.title || r.details}
                            </div>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-gray-600">
                          {r.kind === "NEW" ? (r.category || "—") : (r.asset_name || "—")}
                        </td>
                        <td className="py-3 pr-3 text-gray-600 whitespace-nowrap">
                          {fmtDate(r.submitted_on)}
                        </td>
                        <td className="py-3 pr-3">
                          <Badge className={`${REQUEST_STATUS_BADGE[r.status] || ""} rounded-full`}>
                            {r.status.charAt(0) + r.status.slice(1).toLowerCase()}
                          </Badge>
                        </td>
                        <td className="py-3 pr-3 text-gray-600">
                          {r.decision_on ? (
                            <div>
                              <div className="text-xs">{r.decision_by_name || "—"}</div>
                              {r.decision_note && (
                                <div className="text-xs text-gray-400 max-w-[220px] truncate">
                                  {r.decision_note}
                                </div>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-3 pr-3 text-right">
                          {r.status === "PENDING" && (
                            <button
                              className="gt-btn is-ghost is-sm"
                              onClick={() => withdraw(r.id)}
                            >
                              <X size={14} /> Withdraw
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="space-y-3">
            {/* Type pills — All (n) / Office (n) / Personal (n) */}
            <div className="flex items-center gap-1 flex-wrap">
              {(
                [
                  { key: "ALL", label: `All (${typeCounts.all})` },
                  { key: "OFFICE", label: `Office (${typeCounts.office})` },
                  { key: "PERSONAL", label: `Personal (${typeCounts.personal})` },
                ] as { key: TypeFilter; label: string }[]
              ).map((p) => (
                <button
                  key={p.key}
                  onClick={() => setTypeFilter(p.key)}
                  className={`px-3 py-1.5 text-sm rounded-md border-b-2 transition-colors ${
                    typeFilter === p.key
                      ? "border-emerald-600 text-emerald-700 font-semibold"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Filter row — wraps on narrow screens so every control stays on-screen */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Employee search — only meaningful on All Assets (My Assets are all mine) */}
              {tab === "all" && (
                <div className="flex flex-col">
                  <input
                    type="text"
                    value={empFilter}
                    onChange={(e) => setEmpFilter(e.target.value)}
                    placeholder="Search employee…"
                    aria-label="Filter by assigned employee"
                    list="asset-holder-suggestions"
                    style={{ width: "12rem" }}
                    className="rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                  <datalist id="asset-holder-suggestions">
                    {holderOptions.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                </div>
              )}

              {/* Location filter */}
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                aria-label="Filter by location"
                style={{ width: "11rem" }}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Locations</option>
                {locationOptions.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>

              {/* Status filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
                style={{ width: "10rem" }}
                className="rounded-md border border-gray-300 px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Status</option>
                {Object.entries(STATUS_LABEL).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>

              {/* Asset search with name suggestions (dropdown as you type) */}
              <div className="flex flex-col flex-1 min-w-[12rem]">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search assets…"
                  aria-label="Search assets"
                  list="asset-name-suggestions"
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <datalist id="asset-name-suggestions">
                  {assetNameOptions.map((n) => (
                    <option key={n} value={n} />
                  ))}
                </datalist>
              </div>

              {filtersActive && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="gt-btn is-ghost is-sm"
                >
                  Clear
                </button>
              )}
              <button
                className="gt-btn is-ghost is-sm"
                onClick={loadAssets}
                title="Refresh"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="text-center py-12 text-gray-500 text-sm">
                <Package className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                {tab === "my"
                  ? "No assets are assigned to you yet."
                  : "No assets match your filters."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Asset Name</th>
                      <th className="py-2 pr-3">Asset Type</th>
                      <th className="py-2 pr-3">Ownership</th>
                      <th className="py-2 pr-3">Used For</th>
                      <th className="py-2 pr-3">Location / Use</th>
                      <th className="py-2 pr-3">Assigned On</th>
                      <th className="py-2 pr-3">Status</th>
                      <th className="py-2 pr-3">Current With</th>
                      <th className="py-2 pr-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredAssets.map((a) => {
                      const Icon = categoryIcon(a.category);
                      return (
                        <tr key={a.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-3">
                              {a.image_url ? (
                                <img
                                  src={a.image_url}
                                  alt=""
                                  className="w-9 h-9 rounded-md object-cover bg-gray-100"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-md bg-gray-100 flex items-center justify-center">
                                  <Icon className="w-4 h-4 text-gray-500" />
                                </div>
                              )}
                              <div>
                                <div className="font-medium text-gray-900">{a.name}</div>
                                <div className="text-xs text-gray-500">{a.category || "—"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-3">
                            <Badge
                              className={`rounded-md ${
                                a.asset_type === "OFFICE"
                                  ? "bg-blue-50 text-blue-700"
                                  : "bg-orange-50 text-orange-700"
                              }`}
                            >
                              {a.asset_type === "OFFICE" ? "Office" : "Personal"}
                            </Badge>
                          </td>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-1.5 text-gray-700">
                              {a.ownership === "COMPANY" ? (
                                <Building2 className="w-3.5 h-3.5 text-blue-500" />
                              ) : (
                                <UserRound className="w-3.5 h-3.5 text-orange-500" />
                              )}
                              <div>
                                <div className="text-xs font-medium">
                                  {a.ownership === "COMPANY" ? "Office Asset" : "Personal Asset"}
                                </div>
                                <div className="text-[11px] text-gray-400">
                                  {a.ownership === "COMPANY" ? "Company Owned" : "Employee Owned"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 pr-3">
                            <div className="flex items-center gap-1.5 text-gray-600">
                              {a.used_for === "WFH" ? (
                                <Home className="w-3.5 h-3.5 text-gray-400" />
                              ) : (
                                <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                              )}
                              <span className="text-xs">
                                {a.used_for === "WFH" ? "Work (WFH)" : "Work"}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 pr-3 text-gray-600 text-xs">
                            {a.location || (a.used_for === "WFH" ? "Work From Home" : "—")}
                          </td>
                          <td className="py-3 pr-3 text-gray-600 text-xs whitespace-nowrap">
                            {fmtDate(a.assigned_on)}
                          </td>
                          <td className="py-3 pr-3">
                            <Badge className={`${statusBadgeClass(a.status)} rounded-full`}>
                              {STATUS_LABEL[a.status] || a.status}
                            </Badge>
                            {a.assignment_status === "PENDING" && (
                              <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600">
                                <Clock size={10} /> Awaiting acceptance
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-3">
                            {a.assigned_name ? (
                              <div>
                                <div className="text-xs font-medium text-gray-800">
                                  {a.assigned_name}
                                  {a.assigned_user_id === (user as any)?.id && (
                                    <span className="text-gray-400 font-normal"> (You)</span>
                                  )}
                                </div>
                                {a.assigned_employeeID && (
                                  <div className="text-[11px] text-gray-400 font-mono">
                                    {a.assigned_employeeID}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">In stock</span>
                            )}
                          </td>
                          <td className="py-3 pr-3 text-right whitespace-nowrap">
                            {/* Assignee sign-off — only for MY pending assignments */}
                            {a.assignment_status === "PENDING" &&
                              a.assigned_user_id === (user as any)?.id && (
                                <>
                                  <button
                                    className="gt-btn is-primary is-sm"
                                    onClick={() => doAccept(a)}
                                    disabled={acceptingId === a.id}
                                  >
                                    <Check size={14} /> {acceptingId === a.id ? "…" : "Accept"}
                                  </button>
                                  <button
                                    className="gt-btn is-ghost is-sm text-red-600"
                                    onClick={() => openReject(a)}
                                  >
                                    <X size={14} /> Reject
                                  </button>
                                </>
                              )}
                            <button
                              className="gt-btn is-ghost is-sm"
                              onClick={() => openView(a)}
                            >
                              <Eye size={14} /> View
                            </button>
                            {manageAllowed && tab === "all" && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    className="gt-btn is-ghost is-sm"
                                    aria-label="Asset actions"
                                  >
                                    <MoreHorizontal size={14} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-white">
                                  <DropdownMenuItem onClick={() => openEdit(a)}>
                                    <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                  </DropdownMenuItem>
                                  {a.status !== "RETIRED" && (
                                    <DropdownMenuItem onClick={() => openAssign(a)}>
                                      <ArrowRightLeft className="w-3.5 h-3.5 mr-2" />
                                      {a.assigned_empt_id ? "Transfer" : "Assign"}
                                    </DropdownMenuItem>
                                  )}
                                  {a.assigned_empt_id && (
                                    <DropdownMenuItem onClick={() => doReturn(a)}>
                                      <Undo2 className="w-3.5 h-3.5 mr-2" /> Return to stock
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem
                                    className="text-red-600 focus:text-red-700"
                                    onClick={() => doDelete(a)}
                                  >
                                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="text-xs text-gray-400 mt-3">
                  Showing 1 to {filteredAssets.length} of {filteredAssets.length} assets
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bottom row — usage summary, recent movement, quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Asset Usage Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {!summary || summary.assigned_to_me === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No assets assigned to you yet.
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="relative" style={{ width: 140, height: 140 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Office Assets", value: summary.office_assets },
                          { name: "Personal Assets", value: summary.personal_assets },
                        ].filter((d) => d.value > 0)}
                        dataKey="value"
                        innerRadius={42}
                        outerRadius={62}
                        paddingAngle={2}
                        stroke="none"
                      >
                        <Cell fill="#3b82f6" />
                        <Cell fill="#10b981" />
                      </Pie>
                      <Tooltip formatter={(v: any, n: any) => [`${v}`, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="text-xl font-bold text-gray-900">
                      {summary.assigned_to_me}
                    </div>
                    <div className="text-[10px] text-gray-400">Total</div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#3b82f6" }} />
                    <span className="text-gray-600">Office Assets</span>
                    <span className="font-semibold text-gray-900">{summary.office_assets}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#10b981" }} />
                    <span className="text-gray-600">Personal Assets</span>
                    <span className="font-semibold text-gray-900">{summary.personal_assets}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#f97316" }} />
                    <span className="text-gray-600">WFH Usage</span>
                    <span className="font-semibold text-gray-900">{summary.wfh_assets}</span>
                  </div>
                  <p className="text-[11px] text-gray-400 pt-1">
                    * WFH Usage includes assets used for work from home
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Asset Movement</CardTitle>
          </CardHeader>
          <CardContent>
            {movements.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No movements recorded yet.
              </div>
            ) : (
              <div className="space-y-3">
                {movements.slice(0, 5).map((m) => {
                  const Icon = categoryIcon(m.asset_category);
                  const badge =
                    m.action === "TRANSFERRED"
                      ? { label: "Transfer", cls: "bg-blue-100 text-blue-700" }
                      : m.action === "RETURNED"
                        ? { label: "Return", cls: "bg-gray-100 text-gray-600" }
                        : m.action === "REPAIR"
                          ? { label: "Repair", cls: "bg-amber-100 text-amber-700" }
                          : { label: "New", cls: "bg-green-100 text-green-700" };
                  return (
                    <div key={m.id} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-gray-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {m.asset_name || "Asset"}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {m.action === "TRANSFERRED" && m.from_name && m.to_name
                            ? `${m.from_name} → ${m.to_name}`
                            : m.to_name
                              ? `Assigned to ${m.to_name}`
                              : m.from_name
                                ? `From ${m.from_name}`
                                : "—"}
                          {" · "}
                          {fmtDate(m.moved_on)}
                        </div>
                      </div>
                      <Badge className={`${badge.cls} rounded-md shrink-0`}>{badge.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {(
              [
                {
                  kind: "NEW" as AssetRequestKind,
                  icon: Plus,
                  iconCls: "text-emerald-600 bg-emerald-50",
                  title: "Request New Asset",
                  sub: "Request an asset for office or WFH use",
                },
                {
                  kind: "ISSUE" as AssetRequestKind,
                  icon: AlertTriangle,
                  iconCls: "text-amber-600 bg-amber-50",
                  title: "Repair Request",
                  sub: "Third-party or company repair for an asset",
                },
                {
                  kind: "RETURN" as AssetRequestKind,
                  icon: Undo2,
                  iconCls: "text-red-500 bg-red-50",
                  title: "Return Asset",
                  sub: "Return asset to company",
                },
              ]
            ).map((qa) => (
              <button
                key={qa.kind}
                type="button"
                onClick={() => {
                  resetRequestForm();
                  setReqKind(qa.kind);
                  setRequestOpen(true);
                }}
                className="w-full flex items-center gap-3 rounded-lg p-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${qa.iconCls}`}>
                  <qa.icon size={16} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{qa.title}</span>
                  <span className="block text-xs text-gray-500 truncate">{qa.sub}</span>
                </span>
              </button>
            ))}
            {manageAllowed && (
              <button
                type="button"
                onClick={() => setTab("all")}
                className="w-full flex items-center gap-3 rounded-lg p-2.5 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-blue-600 bg-blue-50">
                  <ArrowRightLeft size={16} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">Assign / Transfer</span>
                  <span className="block text-xs text-gray-500 truncate">
                    Manage the register from All Assets
                  </span>
                </span>
              </button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ------------------------------------------------ View asset dialog */}
      <Dialog open={!!viewAsset} onOpenChange={(open) => !open && setViewAsset(null)}>
        <DialogContent className="max-w-[95%] sm:max-w-lg max-h-[85vh] flex flex-col overflow-hidden rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              {viewAsset?.name}
            </DialogTitle>
          </DialogHeader>
          {viewAsset && (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {[
                  ["Category", viewAsset.category],
                  ["Asset Code", viewAsset.asset_code],
                  ["Serial No.", viewAsset.serial_no],
                  ["Type", viewAsset.asset_type === "OFFICE" ? "Office" : "Personal"],
                  ["Ownership", viewAsset.ownership === "COMPANY" ? "Company Owned" : "Employee Owned"],
                  ["Used For", viewAsset.used_for === "WFH" ? "Work (WFH)" : "Work"],
                  ["Location", viewAsset.location],
                  ["Status", STATUS_LABEL[viewAsset.status] || viewAsset.status],
                  ["Current With", viewAsset.assigned_name || "In stock"],
                  ["Assigned On", fmtDate(viewAsset.assigned_on)],
                  ["Purchase Date", viewAsset.purchase_date ? fmtDate(viewAsset.purchase_date) : null],
                  ["Warranty Until", viewAsset.warranty_until ? fmtDate(viewAsset.warranty_until) : null],
                ]
                  .filter(([, v]) => v)
                  .map(([label, value]) => (
                    <div key={label as string}>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
                      <div className="text-gray-800">{value}</div>
                    </div>
                  ))}
              </div>
              {viewAsset.description && (
                <p className="text-sm text-gray-600">{viewAsset.description}</p>
              )}

              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Movement History
                </div>
                {viewLoading ? (
                  <div className="text-xs text-gray-400 py-2">Loading history…</div>
                ) : (viewAsset.movements || []).length === 0 ? (
                  <div className="text-xs text-gray-400 py-2">No movements recorded.</div>
                ) : (
                  <div className="space-y-2">
                    {(viewAsset.movements || []).map((m) => (
                      <div
                        key={m.id}
                        className="flex items-start gap-2 text-xs border-l-2 border-emerald-200 pl-3 py-1"
                      >
                        <div className="flex-1">
                          <span className="font-medium text-gray-800">
                            {m.action.charAt(0) + m.action.slice(1).toLowerCase()}
                          </span>
                          {m.from_name && <span className="text-gray-500"> from {m.from_name}</span>}
                          {m.to_name && <span className="text-gray-500"> to {m.to_name}</span>}
                          {m.note && <div className="text-gray-400 mt-0.5">{m.note}</div>}
                        </div>
                        <span className="text-gray-400 whitespace-nowrap">{fmtDate(m.moved_on)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ Request dialog */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-md p-5 gap-3 rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" /> Request Asset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>What do you need?</Label>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { key: "NEW", label: "New Asset", icon: Plus },
                    { key: "RETURN", label: "Return", icon: Undo2 },
                    { key: "ISSUE", label: "Repair", icon: AlertTriangle },
                  ] as { key: AssetRequestKind; label: string; icon: any }[]
                ).map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    onClick={() => setReqKind(k.key)}
                    className={`flex items-center justify-center gap-1.5 rounded-md border px-2 py-2 text-xs transition-colors ${
                      reqKind === k.key
                        ? "border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    <k.icon size={15} />
                    {k.label}
                  </button>
                ))}
              </div>
            </div>

            {reqKind === "NEW" ? (
              <div className="space-y-1.5">
                <Label>Asset category</Label>
                <SearchableCombobox
                  value={reqCategory}
                  onChange={setReqCategory}
                  options={categories}
                  placeholder="Select a category…"
                  searchPlaceholder="Search categories…"
                  emptyText="No category found."
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Which asset?</Label>
                <select
                  value={reqAssetId}
                  onChange={(e) => setReqAssetId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select one of your assets…</option>
                  {myAssets.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.category ? ` (${a.category})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {reqKind === "ISSUE" && (
              <div className="space-y-1.5">
                <Label>Who arranges the repair?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setReqRepairMode("THIRD_PARTY")}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${
                      reqRepairMode === "THIRD_PARTY"
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">
                      Third-Party Repair
                    </span>
                    <span className="block text-[11px] leading-snug text-gray-500 mt-0.5">
                      I'll repair it myself & quote the cost
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setReqRepairMode("COMPANY");
                      setReqRepairCost("");
                    }}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${
                      reqRepairMode === "COMPANY"
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">
                      Company Repair
                    </span>
                    <span className="block text-[11px] leading-snug text-gray-500 mt-0.5">
                      Company arranges it — no cost
                    </span>
                  </button>
                </div>
                {reqRepairMode === "THIRD_PARTY" && (
                  <div className="space-y-1.5 pt-1">
                    <Label>Repair cost (₹)</Label>
                    <input
                      type="number"
                      min="0"
                      value={reqRepairCost}
                      onChange={(e) => setReqRepairCost(e.target.value)}
                      placeholder="e.g. 2500"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Details {reqKind === "ISSUE" ? "(what's wrong?)" : "(optional)"}</Label>
              <Textarea
                value={reqDetails}
                onChange={(e) => setReqDetails(e.target.value)}
                rows={2}
                className="text-sm"
                placeholder={
                  reqKind === "NEW"
                    ? "Why do you need it? e.g. laptop for WFH…"
                    : reqKind === "ISSUE"
                      ? "Describe the damage / problem…"
                      : "Anything the approver should know…"
                }
              />
            </div>
          </div>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setRequestOpen(false)}>
              Cancel
            </button>
            <button
              className="gt-btn is-primary"
              onClick={submitRequest}
              disabled={reqSubmitting}
            >
              {reqSubmitting ? "Submitting…" : "Submit Request"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ Add / Edit asset */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-xl max-h-[85vh] flex flex-col overflow-hidden rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              {editingAsset ? "Edit Asset" : "Add Asset"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Asset name *</Label>
                <input
                  type="text"
                  value={form.name || ""}
                  onChange={(e) => setF("name", e.target.value)}
                  placeholder="e.g. Dell Latitude 5420"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <SearchableCombobox
                  value={form.category || ""}
                  onChange={(v) => setF("category", v)}
                  options={categories}
                  placeholder="Select a category…"
                  searchPlaceholder="Search or add a category…"
                  emptyText="Type to add a new category."
                  allowCustom
                />
              </div>
              <div className="space-y-1.5">
                <Label>Asset code / tag</Label>
                <input
                  type="text"
                  value={form.asset_code || ""}
                  onChange={(e) => setF("asset_code", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Serial no.</Label>
                <input
                  type="text"
                  value={form.serial_no || ""}
                  onChange={(e) => setF("serial_no", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Location</Label>
                <input
                  type="text"
                  value={form.location || ""}
                  onChange={(e) => setF("location", e.target.value)}
                  placeholder="Delhi Office / Work From Home"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Asset type</Label>
                <select
                  value={form.asset_type || "OFFICE"}
                  onChange={(e) => setF("asset_type", e.target.value as AssetType)}
                  className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="OFFICE">Office (company owned)</option>
                  <option value="PERSONAL">Personal (employee owned)</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Used for</Label>
                <select
                  value={form.used_for || "WORK"}
                  onChange={(e) => setF("used_for", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="WORK">Work (in office)</option>
                  <option value="WFH">Work From Home</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Purchase date</Label>
                <DateField
                  value={(form.purchase_date as string) || ""}
                  onChange={(v: string) => setF("purchase_date", v || null)}
                  placeholder="Purchase date"
                  size="sm"
                  className="w-full"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Purchase price (₹)</Label>
                <input
                  type="number"
                  min="0"
                  value={form.purchase_price ?? ""}
                  onChange={(e) =>
                    setF("purchase_price", e.target.value === "" ? null : Number(e.target.value))
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Vendor</Label>
                <input
                  type="text"
                  value={form.vendor_name || ""}
                  onChange={(e) => setF("vendor_name", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Warranty until</Label>
                <DateField
                  value={(form.warranty_until as string) || ""}
                  onChange={(v: string) => setF("warranty_until", v || null)}
                  placeholder="Warranty until"
                  size="sm"
                  className="w-full"
                />
              </div>
              {!editingAsset && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Assign to (optional)</Label>
                  <select
                    value={form.assign_to_empt_id || ""}
                    onChange={(e) => setF("assign_to_empt_id", e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Keep in stock (unassigned)</option>
                    {employees.map((e) => (
                      <option key={e.id} value={e.id}>{empLabel(e)}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Notes</Label>
                <Textarea
                  value={form.notes || ""}
                  onChange={(e) => setF("notes", e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setFormOpen(false)}>
              Cancel
            </button>
            <button className="gt-btn is-primary" onClick={saveAsset} disabled={formSaving}>
              {formSaving ? "Saving…" : editingAsset ? "Save Changes" : "Add Asset"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ Assign / Transfer */}
      <Dialog open={!!assignTarget} onOpenChange={(open) => !open && setAssignTarget(null)}>
        <DialogContent className="max-w-[95%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
              {assignTarget?.assigned_empt_id ? "Transfer" : "Assign"} — {assignTarget?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {assignTarget?.assigned_name && (
              <p className="text-sm text-gray-500">
                Currently with <strong>{assignTarget.assigned_name}</strong>.
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Employee</Label>
              <select
                value={assignEmpId}
                onChange={(e) => setAssignEmpId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">Select an employee…</option>
                {employees
                  .filter((e) => e.id !== assignTarget?.assigned_empt_id)
                  .map((e) => (
                    <option key={e.id} value={e.id}>{empLabel(e)}</option>
                  ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                rows={2}
                placeholder="e.g. replacement for the old unit…"
              />
            </div>
          </div>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setAssignTarget(null)}>
              Cancel
            </button>
            <button className="gt-btn is-primary" onClick={doAssign} disabled={assignSaving}>
              {assignSaving
                ? "Saving…"
                : assignTarget?.assigned_empt_id
                  ? "Transfer Asset"
                  : "Assign Asset"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ Reject assignment */}
      <Dialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <DialogContent className="max-w-[95%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="w-5 h-5 text-red-500" /> Reject Asset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              You're declining <strong>{rejectTarget?.name}</strong>. It will go back to
              company stock. Please tell us why.
            </p>
            <div className="space-y-1.5">
              <Label>
                Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder="e.g. Wrong model / already have one / damaged on arrival…"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setRejectTarget(null)}>
              Cancel
            </button>
            <button
              className="gt-btn is-primary"
              onClick={submitReject}
              disabled={rejectSaving || !rejectReason.trim()}
            >
              {rejectSaving ? "Rejecting…" : "Reject Asset"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------------------------------ Request decision */}
      <Dialog open={!!decisionReq} onOpenChange={(open) => !open && setDecisionReq(null)}>
        <DialogContent className="max-w-[95%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" /> Review Request
            </DialogTitle>
          </DialogHeader>
          {decisionReq && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div>
                  <span className="text-gray-500">From:</span>{" "}
                  <strong>{decisionReq.name || "—"}</strong>
                  {decisionReq.employeeID && (
                    <span className="text-gray-400 font-mono text-xs"> ({decisionReq.employeeID})</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-500">Request:</span>{" "}
                  {REQUEST_KIND_LABEL[decisionReq.kind] || decisionReq.kind}
                  {decisionReq.kind === "NEW"
                    ? ` — ${decisionReq.category || "—"}`
                    : ` — ${decisionReq.asset_name || "—"}`}
                </div>
                {decisionReq.details && (
                  <div className="text-gray-600 text-xs whitespace-pre-wrap">{decisionReq.details}</div>
                )}
                {decisionReq.kind === "ISSUE" && (
                  <>
                    {decisionReq.repair_mode && (
                      <div>
                        <span className="text-gray-500">Repair:</span>{" "}
                        {decisionReq.repair_mode === "THIRD_PARTY"
                          ? `Third-party (arranged by employee) — ${fmtMoney(decisionReq.repair_cost)}`
                          : "By company (no cost quoted)"}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-amber-600 text-xs">
                      <AlertTriangle size={12} /> Approving moves the asset to Under Repair.
                    </div>
                  </>
                )}
                {decisionReq.kind === "RETURN" && (
                  <div className="flex items-center gap-1 text-blue-600 text-xs">
                    <Undo2 size={12} /> Approving returns the asset to stock.
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Textarea
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                  rows={2}
                  placeholder="Visible to the requester…"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button
              className="gt-btn is-ghost"
              onClick={() => submitDecision("REJECTED")}
              disabled={decisionSaving}
            >
              <X size={14} /> Reject
            </button>
            <button
              className="gt-btn is-primary"
              onClick={() => submitDecision("APPROVED")}
              disabled={decisionSaving}
            >
              <Check size={14} /> {decisionSaving ? "Saving…" : "Approve"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
